// App.js
import React, { useEffect, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import RootRoutes from "./components/Auth/RootRoutes";
import UserContext from "./components/context/User";
import {
  getUserFullDetails,
  getUserToken,
  getUserTokenJWT,
} from "./utils/api/accounts/index";
import { useLocation, useNavigate } from "react-router";
import {
  withAITracking,
  AppInsightsContext,
} from "@microsoft/applicationinsights-react-js";
import {
  reactPlugin,
  appInsights,
} from "./components/userlogin/azureConfig/AppInsights";
import theme from "./theme";
var CryptoJS = require("crypto-js");

function App() {
  let navigate = useNavigate();
  const [userData, setUserData] = useState({});
  const location = useLocation();
  const [justLoggedOut, setJustLoggedOut] = useState(false); // Track logout state

  const [defaultUser] = useState({
    username: process.env.REACT_APP_DEFAULT_USERNAME,
    password: process.env.REACT_APP_DEFAULT_PASSWORD,
  });

  function getTokenFromUrl() {
    const query = new URLSearchParams(location.search);
    const token = query.get("token");
    console.log("Token from URL:", token);
    if (token) {
      localStorage.setItem("queryToken", token);
    }
    return token;
  }

  async function handleDefaultLogin() {
    try {
      let temp_get_token = {};
      let temp_get_details = {};
      let breaking_manually_1 = 0;
      while (true) {
        temp_get_token = await getUserToken(defaultUser);
        console.log("Default login - temp_get_token:", temp_get_token);
        breaking_manually_1++;
        if (breaking_manually_1 === 10 || temp_get_token.status) {
          break;
        }
      }

      if (temp_get_token.status) {
        let breaking_manually_2 = 0;
        while (true) {
          temp_get_details = await getUserFullDetails(
            temp_get_token.response.jwtToken,
            defaultUser
          );
          console.log("Default login - temp_get_details:", temp_get_details);
          breaking_manually_2++;
          if (breaking_manually_2 === 10 || temp_get_details.status) {
            break;
          }
        }

        if (temp_get_details.status) {
          const token = temp_get_token.response.jwtToken;
          if (!token) {
            throw new Error("No token found in default login response");
          }
          localStorage.setItem("defaultToken", token);
          var ciphertext = CryptoJS.AES.encrypt(
            JSON.stringify({
              ...defaultUser,
              token: token,
              message: "Default Login Successful",
              isLoggedIn: true,
              fullDetails: temp_get_details.response,
            }),
            process.env.REACT_APP_CRYPTING_KEY
          ).toString();

          localStorage.setItem("defLoginCrypt", ciphertext);
          setUserData((prev) => ({
            ...prev,
            defaultLogin: {
              ...defaultUser,
              token: token,
              message: "Default Login Successful",
              isLoggedIn: true,
              fullDetails: temp_get_details.response,
            },
          }));
          console.log("Default login - Token saved:", token);
        } else {
          throw new Error("Failed to get user details in default login");
        }
      } else {
        throw new Error("Failed to get token in default login");
      }
    } catch (error) {
      console.error("Default login failed:", error.message);
    }
  }

  async function handleLogin(userloginDetail) {
    try {
      let temp_get_token = await getUserTokenJWT(userloginDetail);
      console.log("User login - temp_get_token:", temp_get_token);

      if (temp_get_token.status) {
        let temp_get_details = await getUserFullDetails(
          temp_get_token.response.jwtToken,
          userloginDetail
        );
        console.log("User login - temp_get_details:", temp_get_details);

        if (temp_get_details.status) {
          const token = temp_get_token.response.jwtToken;
          if (!token) {
            throw new Error("No token found in user login response");
          }
          const loggedinUser = {
            ...userloginDetail,
            token: token,
            isLoggedIn: true,
            fullDetails: temp_get_details.response,
          };

          localStorage.setItem("jwtToken", token);
          localStorage.setItem(
            "UserDetails",
            JSON.stringify(temp_get_details.response)
          );
          localStorage.setItem("loggedIn", true);

          setUserData((prev) => ({
            ...prev,
            loggedinUser,
          }));
          console.log("User login - Token saved:", token);
          return loggedinUser;
        } else {
          throw new Error("Failed to get user details in user login");
        }
      } else {
        throw new Error(temp_get_token?.message || "Failed to get token in user login");
      }
    } catch (error) {
      console.error("User login failed:", error.message);
      return {
        isLoggedIn: false,
        message: error.message,
        // code: temp_get_token?.code,
      };
    }
  }

  async function handleTokenLogin(token) {
    try {
      const userloginDetail = { token };
      let loggedinUser = {
        ...userloginDetail,
        isLoggedIn: false,
      };

      const temp_get_details = await getUserFullDetails(token, userloginDetail);
      console.log("Token login - temp_get_details:", temp_get_details);

      if (temp_get_details.status) {
        if (!token) {
          throw new Error("No token provided for token login");
        }
        localStorage.setItem("jwtToken", token);
        localStorage.setItem("loggedIn", true);

        loggedinUser = {
          ...userloginDetail,
          token,
          isLoggedIn: true,
          fullDetails: temp_get_details.response,
        };

        const ciphertext = CryptoJS.AES.encrypt(
          JSON.stringify(loggedinUser),
          process.env.REACT_APP_CRYPTING_KEY
        ).toString();
        localStorage.setItem("loginData", ciphertext);

        setUserData((prev) => ({
          ...prev,
          loggedinUser,
        }));
        console.log("Token login - Token saved:", token);
        navigate("/");
      } else {
        throw new Error("Failed to get user details in token login");
      }
    } catch (error) {
      console.error("Token login failed:", error.message);
    }
  }

  async function handleLogout() {
    console.log("Logging out - Clearing localStorage");
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("oldLoginCrypt");
    localStorage.removeItem("defLoginCrypt");
    localStorage.removeItem("defaultToken");
    localStorage.removeItem("loginData");
    localStorage.removeItem("queryToken");
    localStorage.removeItem("loggedIn");
    localStorage.removeItem("dashboard_status");
    localStorage.removeItem("firstName");
    localStorage.removeItem("userOrganisation");
    localStorage.removeItem("UserDetails");
    localStorage.removeItem("iframe");
    localStorage.removeItem("isPortalView");

    setUserData((prev) => ({
      ...prev,
      loggedinUser: {},
      defaultLogin: {},
    }));
    setJustLoggedOut(true); // Mark logout state
    navigate("/", { replace: true });
    // navigate("/login", { replace: true });
  }

  async function automatedLogin() {
    if (justLoggedOut) {
      console.log("Skipping automatedLogin due to recent logout");
      setJustLoggedOut(false); // Reset after one run
      return;
    }

    let defLoginCrypt = {
      status: false,
      data: localStorage.getItem("defLoginCrypt"),
      conv: {},
    };
    let oldLoginCrypt = {
      status: false,
      data: localStorage.getItem("oldLoginCrypt"),
      conv: {},
    };

    if (defLoginCrypt.data) {
      try {
        let bytes = CryptoJS.AES.decrypt(
          defLoginCrypt.data,
          process.env.REACT_APP_CRYPTING_KEY
        );
        let decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
        defLoginCrypt.conv = { ...decryptedData };
        defLoginCrypt.status = true;
      } catch (error) {
        console.error("Failed to decrypt defLoginCrypt:", error.message);
      }
    }

    if (oldLoginCrypt.data) {
      try {
        let bytes = CryptoJS.AES.decrypt(
          oldLoginCrypt.data,
          process.env.REACT_APP_CRYPTING_KEY
        );
        let decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
        oldLoginCrypt.conv = { ...decryptedData };
        oldLoginCrypt.status = true;
      } catch (error) {
        console.error("Failed to decrypt oldLoginCrypt:", error.message);
      }
    }

    // Only trigger default login if no valid user login and no existing jwtToken
    if (!oldLoginCrypt.status && !defLoginCrypt.status && !localStorage.getItem("jwtToken")) {
      console.log("No valid login found, attempting default login");
      localStorage.setItem("jwtToken", "");
      localStorage.setItem("defLoginCrypt", "");
      localStorage.setItem("oldLoginCrypt", "");
      localStorage.setItem("loggedIn", false);
      await handleDefaultLogin();
    }

    if (defLoginCrypt.status && oldLoginCrypt.status) {
      setUserData((prev) => ({
        ...prev,
        defaultLogin: { ...defLoginCrypt.conv },
        loggedinUser: { ...oldLoginCrypt.conv },
      }));
      try {
        if (window.location.pathname !== "/forgot/password/create") {
          navigate(-1);
        }
      } catch (err) {
        setTimeout(() => navigate("/"), 2000);
      }
    } else if (defLoginCrypt.status) {
      setUserData((prev) => ({
        ...prev,
        defaultLogin: { ...defLoginCrypt.conv },
      }));
    }
  }

  useEffect(() => {
    const token = getTokenFromUrl();
    if (token) {
      handleTokenLogin(token);
    } else {
      automatedLogin();
    }
  }, [location]);

  return (
    <section data-test-id="app-1">
      <AppInsightsContext.Provider value={reactPlugin}>
        <ThemeProvider theme={theme}>
          <UserContext.Provider
            value={{
              ...userData,
              handleLogin,
              handleLogout,
            }}
          >
            <RootRoutes />
          </UserContext.Provider>
        </ThemeProvider>
      </AppInsightsContext.Provider>
    </section>
  );
}

export default withAITracking(reactPlugin, App);